import { TaskDrawerActionItem } from './TaskDrawerActionItem';
import { CircularProgress, Button, Stack, Typography, useTheme } from '@mui/material';
import {
  ActionExecuteStatusEnum,
  ActionTypeNameEnum,
  useEditorStore,
  Menu,
  IMenuAction,
} from '@diana/core';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useFindActiveTask } from '../../Support/useFindActiveTask';
import { useCallback, useMemo, useState } from 'react';
import { useAddAction } from '../../Support/useAddAction';
import { AddRounded as AddIcon } from '@mui/icons-material';

export const TaskDrawerActionList = () => {
  const { palette } = useTheme();
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const findActiveTask = useFindActiveTask();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const actionSetId = useEditorStore((s) => findActiveTask(s.flow)?.actionSetId ?? '');
  const actions = useEditorStore((s) => findActiveTask(s.flow)?.actions ?? [], isEqual);

  const { addAction, isAddingAction } = useAddAction();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl],
  );

  const handleClose = useCallback(
    (event?: any) => {
      event && event.stopPropagation();
      setAnchorEl(null);
    },
    [setAnchorEl],
  );

  const handleMenuItemClick = useCallback(
    ({ isStopAction = false }: { isStopAction?: boolean }) => {
      const data = {
        displayName: isStopAction ? t('labels.end') : t('labels.submit'),
        description: isStopAction
          ? t('descriptions.endThisWork')
          : t('descriptions.forwardToNextTask'),
        ...(isStopAction && {
          actionTypeName: ActionTypeNameEnum.Stop,
          executeStatus: ActionExecuteStatusEnum.Declined,
        }),
      };

      addAction(actionSetId, data).catch();
      handleClose();
    },
    [actionSetId, addAction, handleClose, t],
  );

  const menuActions: IMenuAction[] = useMemo(() => {
    return [
      { title: t('labels.submit'), onClick: () => handleMenuItemClick({ isStopAction: false }) },
      { title: t('labels.end'), onClick: () => handleMenuItemClick({ isStopAction: true }) },
    ];
  }, [t, handleMenuItemClick]);

  return (
    <>
      <Stack gap={0.5}>
        <Stack justifyContent="center" sx={{ height: 36 }}>
          <Typography color={palette.text.secondary}>{t('titles.taskActions')}</Typography>
        </Stack>
        <Stack gap={1}>
          {actions.map((action, index) => {
            return (
              <TaskDrawerActionItem
                key={`task-panel-action-${index}`}
                index={index}
                action={action}
              />
            );
          })}

          <Button
            sx={{ justifyContent: 'left', textTransform: 'none', borderRadius: 1.5 }}
            disabled={isAddingAction}
            startIcon={
              isAddingAction ? <CircularProgress size="1em" /> : <AddIcon color="primary" />
            }
            onClick={handleClick}
            fullWidth
          >
            {t('labels.addAction')}
          </Button>
        </Stack>
      </Stack>
      <Menu
        position="bottomLeftInner"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        actions={menuActions}
      />
    </>
  );
};
